import React from "react";
import { SortDropdownPagination } from "../components";
import { useState, useMemo, useEffect } from "@/common/hooks";
import { useRef } from "react";

const MobileResponsiveGrid = ({
  wrapperClassNames = "",
  columnDefs,
  rowData = [],
  onCellValueChanged,
  itemsPerPage = 3,
  showSortDropDown = false,
  showPagination = true,
  onRowClick = () => {},
  onCellClicked = () => {},
  getRowClass = () => {},
  noDataLabel = "No data available",
  children,
  enableCheckboxSelection = false,
  onSelectionChanged = () => {},
  sortOption = {},
  setSortOption = {},
  dropdownData = [],
  resetPageOnDataChange = true,
  // NEW optional prop (backwards-compatible)
  selectedRowId,
}) => {
  const [editingCells, setEditingCells] = useState({}); // key: `${rowIndex}-${field}` => true/false
  const [editingDrafts, setEditingDrafts] = useState({}); // key => string

  const cellKeyOf = (rowIndex, field) => `${rowIndex}-${field}`;

  const startEdit = (rowIndex, field) => {
    const key = cellKeyOf(rowIndex, field);
    setEditingCells((m) => ({ ...m, [key]: true }));
    setEditingDrafts((m) => ({ ...m, [key]: m[key] ?? "" })); // keep if already present
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedCells, setExpandedCells] = useState({});

  const prevRowIdsRef = useRef([]);
  /* ====================== ADDED: helpers for sorting dropdown & state ====================== */
  const isFn = (f) => typeof f === "function";
  const [internalSortOption, setInternalSortOption] = useState("");

  // Prefer external sortOption/setSortOption if provided correctly; else use internal
  const useExternalSort = isFn(setSortOption);
  const activeSortOption = useExternalSort ? sortOption : internalSortOption;
  const setActiveSortOption = useExternalSort
    ? setSortOption
    : setInternalSortOption;

  // Build dropdown options from columnDefs header
  const computedDropdownData = useMemo(() => {
    const options = [];
    const ascText = "ASC to DESC";
    const descText = "DESC to ASC";

    (columnDefs || [])
      .filter((c) => !c.hide && (c.headerName || c.field))
      .forEach((c) => {
        const header = c.headerName ?? c.field ?? "Column";
        options.push(`${header} (${ascText})`);
        options.push(`${header} (${descText})`);
      });

    return options;
  }, [columnDefs]);

  // Merge provided dropdownData with computed column-based ones (unique, keep order)
  const finalDropdownData = useMemo(() => {
    const base = Array.isArray(dropdownData) ? dropdownData : [];
    const seen = new Set();
    const merged = [];
    for (const o of base) {
      if (!seen.has(o)) {
        seen.add(o);
        merged.push(o);
      }
    }
    for (const o of computedDropdownData) {
      if (!seen.has(o)) {
        seen.add(o);
        merged.push(o);
      }
    }
    return merged;
  }, [dropdownData, computedDropdownData]);

  // Parse selected option like "Status (A to Z)" -> { header: "Status", dir: "asc" }
  const parseSortSelection = (sel) => {
    if (typeof sel === "string") {
      // Accept new labels and old ones (A to Z / Z to A) for compatibility
      const m = sel.match(
        /^(.*)\s+\((A to Z|Z to A|ASC to DESC|DESC to ASC|ASC|DESC)\)$/i
      );
      if (m) {
        const header = m[1].trim();
        const token = m[2].toUpperCase();

        const dir =
          token === "A TO Z" || token === "ASC TO DESC" || token === "ASC"
            ? "asc"
            : "desc";

        return { header, dir };
      }
    } else if (sel && typeof sel === "object") {
      // Object form still supported: { header, field, dir }
      return {
        header: sel.header,
        field: sel.field,
        dir: sel.dir === "desc" ? "desc" : "asc",
      };
    }
    return null;
  };

  const resolveColByHeaderOrField = (header, field) => {
    if (field) return (columnDefs || []).find((c) => c.field === field);
    return (columnDefs || []).find((c) => (c.headerName ?? c.field) === header);
  };

  const naturalCompare = (a, b) =>
    String(a ?? "").localeCompare(String(b ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  /* ========================================================================================= */

  // Utility to resolve value via valueGetter or field path
  const getFieldValue = (colDef, entity) => {
    if (colDef?.valueGetter) {
      return colDef.valueGetter({ data: entity });
    }
    return colDef?.field?.split(".").reduce((obj, key) => obj?.[key], entity);
  };

  /* ====================== UPDATED: sorting uses dropdown selection first ====================== */
  const sortedRowData = useMemo(() => {
    const sel = parseSortSelection(activeSortOption);
    let colToSort = null;
    let dir = "asc";

    if (sel) {
      colToSort = resolveColByHeaderOrField(sel.header, sel.field);
      dir = sel.dir;
    }

    // Fallback to any colDef with default `sort` set
    if (!colToSort) {
      const defaultSorted = (columnDefs || []).find((col) => col.sort);
      if (!defaultSorted) return rowData;
      colToSort = defaultSorted;
      dir = defaultSorted.sort === "desc" ? "desc" : "asc";
    }

    const copy = [...rowData];
    copy.sort((a, b) => {
      const va = getFieldValue(colToSort, a);
      const vb = getFieldValue(colToSort, b);
      const cmp = naturalCompare(va, vb);
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rowData, columnDefs, activeSortOption]);
  /* ============================================================================================ */

  const paginatedRowData = useMemo(() => {
    const start = currentPage * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedRowData.slice(start, end);
  }, [sortedRowData, currentPage, itemsPerPage]);

  const renderCellValue = (field, entity, rowIndex) => {
    const value = getFieldValue(field, entity);
    const key = `${rowIndex}-${field.field}`;
    // 1) If a function is provided, call with params (AG Grid-like)
    if (typeof field.cellRenderer === "function") {
      const params = {
        data: entity,
        value,
        rowIndex,
        colDef: field,
        node: { rowIndex },
        ...field.cellRendererParams, // any extra props from parent
      };
      return field.cellRenderer(params);
    }

    // 2) If a React element was passed, clone it with the same params
    if (React.isValidElement(field.cellRenderer)) {
      const extraProps = {
        data: entity,
        value,
        rowIndex,
        colDef: field,
        ...(field.cellRendererProps || field.cellRendererParams),
      };
      return React.cloneElement(field.cellRenderer, extraProps);
    }

    if (field.clickToExpand) {
      const cellKey = `${rowIndex}-${field.field}`;
      const isExpanded = !!expandedCells[cellKey];
      const truncateLength = field.truncateLength ?? 30;

      const shouldTruncate = value?.length > truncateLength && !isExpanded;
      const displayText = shouldTruncate
        ? value.slice(0, truncateLength) + "..."
        : value;

      const toggleExpand = (e) => {
        e.stopPropagation();
        setExpandedCells((prev) => ({
          ...prev,
          [cellKey]: !prev[cellKey],
        }));
      };

      return (
        <span onClick={toggleExpand} style={{ cursor: "pointer" }}>
          {displayText}
          {shouldTruncate && <span className="primary-color ms-1">(more)</span>}
        </span>
      );
    }

    if (field.cellRenderer === "agCheckboxCellRenderer") {
      const isDisabled =
        typeof field.cellRendererParams?.disabled === "function"
          ? field.cellRendererParams.disabled({ data: entity, rowIndex })
          : !!field.cellRendererParams?.disabled;

      const handleToggle = (e) => {
        e.stopPropagation();
        const next = e.target.checked;
        field.cellRendererParams?.onChange?.({
          next,
          rowIndex,
          field: field.field,
          data: entity,
          colDef: field,
        });
      };

      return (
        <input
          type="checkbox"
          checked={!!getFieldValue(field, entity)}
          disabled={isDisabled}
          onChange={handleToggle}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    // 5) Editable cells (this was where `colDef` slipped in)
    if (field.editable) {
      const isEditing = !!editingCells[key];

      // not editing: show formatted text
      if (!isEditing) {
        const display =
          typeof field.valueFormatter === "function"
            ? field.valueFormatter({ data: entity, value, colDef: field })
            : value ?? "";

        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
              // initialize draft with the *raw* current value (not formatted)
              setEditingDrafts((m) => ({ ...m, [key]: String(value ?? "") }));
              startEdit(rowIndex, field.field);
            }}
            style={{ cursor: "text" }}
            role="button"
          >
            {display}
          </span>
        );
      }

      // editing: show input bound to draft
      const draft = editingDrafts[key] ?? String(value ?? "");

      const commit = (rawText) => {
        // Build AG Grid–like parser params
        const parserParams = {
          newValue: rawText,
          oldValue: value,
          data: entity,
          colDef: field,
          value: rawText, // also provide .value for older helpers
        };

        let parsed = rawText;
        if (typeof field.valueParser === "function") {
          try {
            parsed = field.valueParser(parserParams);
          } catch {
            // fall back to raw text if parser throws
            parsed = rawText;
          }
        }

        // Let column-level onChange win; else use grid-level onCellValueChanged
        const payload = {
          next: parsed,
          text: rawText,
          rowIndex,
          field: field.field,
          data: entity,
          colDef: field,
        };

        if (typeof field.onChange === "function") {
          field.onChange(payload);
        } else if (typeof onCellValueChanged === "function") {
          onCellValueChanged(payload);
        }

        // Exit edit & clear draft
        setEditingCells((m) => ({ ...m, [key]: false }));
        setEditingDrafts((m) => {
          const { [key]: _, ...rest } = m;
          return rest;
        });
      };

      return (
        <input
          type={field.inputType || "text"}
          inputMode={field.inputMode || "decimal"}
          value={draft}
          onChange={(e) =>
            setEditingDrafts((m) => ({ ...m, [key]: e.target.value }))
          }
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(e.currentTarget.value);
          }}
          onClick={(e) => e.stopPropagation()}
          className="form-control py-2"
          aria-label={field.headerName || field.field}
          autoFocus
        />
      );
    }

    return value;
  };

  const toggleRowSelection = (entity) => {
    const isSelected = selectedRows.includes(entity);
    const updatedSelection = isSelected
      ? selectedRows.filter((row) => row !== entity)
      : [...selectedRows, entity];

    setSelectedRows(updatedSelection);
    onSelectionChanged(updatedSelection);
  };

  useEffect(() => {
    if (!resetPageOnDataChange) return;
    const currIds =
      Array.isArray(rowData) && rowData.length
        ? rowData.map((r) => String(r?.id ?? "")).join("|")
        : `len:${rowData?.length ?? 0}|first:${JSON.stringify(
            rowData?.[0] ?? ""
          )}|last:${JSON.stringify(rowData?.[rowData?.length - 1] ?? "")}`;

    const prevIds = prevRowIdsRef.current.join
      ? prevRowIdsRef.current.join("|")
      : String(prevRowIdsRef.current);

    // Only reset page when the signature (content) actually changed
    if (prevIds !== currIds) {
      setCurrentPage(0);
      // keep as an array of ids for easier future checks
      prevRowIdsRef.current = Array.isArray(rowData)
        ? rowData.map((r) => String(r?.id ?? ""))
        : [];
    }
    // NOTE: we intentionally *do not* reset page when only selection/parent state changes
  }, [rowData, itemsPerPage, activeSortOption, resetPageOnDataChange]);

  useEffect(() => {
    setSelectedRows([]);
    onSelectionChanged([]);
  }, [rowData]);

  useEffect(() => {
    const maxPage = Math.ceil(sortedRowData.length / itemsPerPage);
    if (currentPage > maxPage - 1) {
      setCurrentPage(maxPage - 1);
    }
  }, [sortedRowData, currentPage, itemsPerPage]);

  const renderRowTable = (entity, globalRowIndex) => (
    <div className="mobile-grid-card border-bottom p-3 mb-2">
      {columnDefs.map((field, i) => {
        if (field.hide) return null;

        const isCheckboxColumn = enableCheckboxSelection && i === 1;

        return (
          <div
            key={i}
            className="d-flex justify-content-between align-items-start mb-2 small"
            onClick={() => onCellClicked({ data: entity, colDef: field })}
          >
            <div className="fw-bold align-self-center">
              {isCheckboxColumn && (
                <input
                  type="checkbox"
                  checked={selectedRows.includes(entity)}
                  onChange={() => toggleRowSelection(entity)}
                  className="me-2 align-middle mobile-grid-checkbox"
                />
              )}
              {field.headerName}
            </div>
            <div className="text-end text-break ms-2">
              {renderCellValue(field, entity, globalRowIndex)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    // new (visible up to md, hidden on lg and above)
    <div
      className={`d-lg-none d-print-none d-block only-ipad ${wrapperClassNames}`}
    >
      {children}
      {rowData?.length === 0 ? (
        <p className="py-3 text-center">{noDataLabel}</p>
      ) : (
        <>
          {showPagination && (
            <SortDropdownPagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              allEntities={sortedRowData}
              itemsPerPage={itemsPerPage}
              showSortDropDown={showSortDropDown}
              sortOption={activeSortOption}
              setSortOption={setActiveSortOption}
              dropdownData={finalDropdownData}
            />
          )}
          <div>
            {paginatedRowData.map((entity, index) => {
              const globalRowIndex = currentPage * itemsPerPage + index;
              // First try custom getRowClass (AG Grid-like params). If it returns falsy,
              // fallback to selectedRowId prop (if present) to apply highlighting.
              const customClass = getRowClass({ data: entity }) || "";
              const selectedClass =
                selectedRowId && entity?.id === selectedRowId ? "hover-bg" : "";
              const rowClass = customClass || selectedClass;

              /* ======= CRITICAL: call onRowClick with a backward-compatible shape =======
               * We pass an object that contains entity fields directly (so callers expecting
               * the raw entity can use `row?.id`) and also a `.data` property (so callers
               * expecting an AG Grid–like event can use `e.data.id`).
               * This is a single call (no double-calls) to avoid duplicate side effects.
               * ======================================================================= */
              const clickPayload = { ...entity, data: entity };

              return (
                <div
                  key={index}
                  className={rowClass ? rowClass : ""}
                  onClick={() => onRowClick(clickPayload)}
                >
                  {renderRowTable(entity, globalRowIndex)}
                </div>
              );
            })}
          </div>
          {showPagination && (
            <SortDropdownPagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              allEntities={sortedRowData}
              itemsPerPage={itemsPerPage}
              showSortDropDown={false}
              sortOption={activeSortOption}
              setSortOption={setActiveSortOption}
              dropdownData={finalDropdownData}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MobileResponsiveGrid;
